"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const stats = [
  { value: 80, suffix: "+", label: "Ports Served", icon: "⚓" },
  { value: 2500, suffix: "+", label: "Vessels Served", icon: "🚢" },
  { value: 150, suffix: "+", label: "Global Clients", icon: "🤝" },
  { value: 97, suffix: "%", label: "Client Satisfaction", icon: "⭐" },
];

function useCountUp(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({ stat, index, animate }: { stat: typeof stats[0]; index: number; animate: boolean }) {
  const count = useCountUp(stat.value, 2200, animate);
  return (
    <div
      className={`glass-card rounded-2xl p-5 flex flex-col gap-1 hover-lift cursor-default float-${(index % 3) + 1}`}
      style={{ animationDelay: `${index * 0.5}s` }}
    >
      <span className="text-2xl">{stat.icon}</span>
      <div className="text-3xl font-black font-display text-white leading-none">
        {count}{stat.suffix}
      </div>
      <div className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-semibold">{stat.label}</div>
    </div>
  );
}

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [animateStats, setAnimateStats] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      img: "https://customsgov.co/wallpaper.jpg",
      alt: "Large cargo vessel at sea during golden hour, BlueSea Marine Services",
    },
    {
      img: "https://customsgov.co/wallpaper-1.jpg",
      alt: "Commercial ship at port with industrial cranes in background",
    },
    {
      img: "https://customsgov.co/wallpaper-3.jpg",
      alt: "Ocean freighter navigating open sea under blue sky",
    },
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setAnimateStats(true), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      style={{ minHeight: "100svh" }}
    >
      {/* Background slides */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-1500"
          style={{
            opacity: currentSlide === i ? 1 : 0,
            transform: `translateY(${scrollY * 0.3}px) scale(${1 + scrollY * 0.0002})`,
            transition: currentSlide === i ? "opacity 1.5s ease" : "opacity 1.5s ease",
          }}
        >
          <AppImage
            src={slide.img}
            alt={slide.alt}
            fill
            className="object-cover"
            priority={i === 0}
          />
        </div>
      ))}

      {/* Grid overlay */}
      <div className="absolute inset-0 hero-grid z-10 pointer-events-none" />

      {/* Gradient overlays */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-navy-900 via-navy-900/70 to-navy-900/30 pointer-events-none" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-navy-900/60 via-transparent to-navy-900/20 pointer-events-none" />

      {/* Floating top badge */}
      <div className="absolute top-32 right-8 md:right-16 z-30 hidden lg:flex">
        <div className="rotating-badge">
          <svg className="animate-spin-slow w-full h-full" viewBox="0 0 120 120">
            <path
              id="badgePath"
              d="M 60,60 m -45,0 a 45,45 0 1,1 90,0 a 45,45 0 1,1 -90,0"
              fill="transparent"
            />
            <text fontSize="9.5" fontFamily="DM Sans" fontWeight="700" letterSpacing="3.5" fill="rgba(200,168,75,0.8)">
              <textPath href="#badgePath" startOffset="0%">
                MARPOL COMPLIANT · IMO CERTIFIED ·
              </textPath>
            </text>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl">⚓</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-20 max-w-7xl mx-auto w-full px-6 md:px-10 pb-16 md:pb-24 pt-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">

          {/* Left: Hero text */}
          <div className="lg:col-span-7 space-y-8">
            {/* Eyebrow */}
            <div
              className="inline-flex items-center gap-3 px-4 py-2 rounded-full compliance-badge"
              style={{ animation: "fadeUp 0.8s cubic-bezier(0.2,1,0.3,1) 0.2s both" }}
            >
              <span className="w-2 h-2 rounded-full bg-ocean-400 animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-gold-400 font-bold">
                Est. 2016 · Mumbai, India
              </span>
            </div>

            {/* Headline */}
            <div style={{ animation: "fadeUp 0.8s cubic-bezier(0.2,1,0.3,1) 0.35s both" }}>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.88] tracking-tight">
                <span className="text-white block">Marine</span>
                <span className="text-gradient-ocean block italic">Services</span>
                <span className="text-white block">You Can</span>
                <span className="text-gradient-gold block italic">Trust.</span>
              </h1>
            </div>

            {/* Subheadline */}
            <p
              className="text-lg md:text-xl text-[#8BA3BC] leading-relaxed max-w-xl font-medium"
              style={{ animation: "fadeUp 0.8s cubic-bezier(0.2,1,0.3,1) 0.5s both" }}
            >
              Waste management, tank cleaning, supply services & marine surveys — 
              delivered across <span className="text-white font-semibold">80+ ports</span> in India, UAE, Bangladesh & Sri Lanka.
            </p>

            {/* CTAs */}
            <div
              className="flex flex-wrap gap-4"
              style={{ animation: "fadeUp 0.8s cubic-bezier(0.2,1,0.3,1) 0.65s both" }}
            >
              <a
                href="#contact"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ocean-600 hover:bg-ocean-500 text-white font-bold text-sm uppercase tracking-widest transition-all duration-300 shadow-ocean hover:shadow-glow-blue hover:-translate-y-1"
              >
                Request Service
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                </svg>
              </a>
              <a
                href="#services"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/15 hover:border-ocean-400/50 text-white font-bold text-sm uppercase tracking-widest transition-all duration-300 hover:bg-white/5 hover:-translate-y-1"
              >
                Our Services
              </a>
            </div>
          </div>

          {/* Right: Floating stat cards */}
          <div
            className="lg:col-span-5 grid grid-cols-2 gap-4"
            style={{ animation: "slideInRight 0.8s cubic-bezier(0.2,1,0.3,1) 0.5s both" }}
          >
            {stats.map((stat, i) => (
              <StatCard key={i} stat={stat} index={i} animate={animateStats} />
            ))}
          </div>
        </div>

        {/* Slide indicators */}
        <div className="flex items-center gap-2 mt-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`transition-all duration-500 rounded-full ${
                i === currentSlide
                  ? "w-8 h-1.5 bg-ocean-400" :"w-1.5 h-1.5 bg-white/20 hover:bg-white/40"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 opacity-60">
        <span className="text-[10px] uppercase tracking-widest text-[#8BA3BC]">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-ocean-400 to-transparent animate-pulse" />
      </div>
    </section>
  );
}